import type {ComponentType, ReactNode} from 'react';

import {view} from '@sail/ui';

import {Conversation} from '@stripe-internal/conversations-client';
import {ConversationActivityIndicatorProvider} from '@stripe-internal/conversations-ui';

import {ComponentWithOverride} from '../../../core/components/ComponentWithOverride';
import {LoadingScreen} from '../../../core/components/LoadingScreen';
import {ThreadsConversationInput} from './ThreadsConversationInput';
import {ThreadsConversationLog} from './ThreadsConversationLog';
import {useThreadsConversationExceptions} from '../../hooks/useThreadsConversationExceptions';
import {useThreadsConversationInlineDataEvents} from '../../hooks/useThreadsConversationInlineDataEvents';
import {useThreadsConversationViewModelState} from '../../app/hooks';

type Props = {
  conversation?: Conversation | null;
  log?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
  input?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
};

export function ThreadsConversationScreenContent({
  conversation,
  log,
  input,
}: Props) {
  const {loading} = useThreadsConversationViewModelState();

  useThreadsConversationExceptions(conversation);
  useThreadsConversationInlineDataEvents(conversation);

  if (loading && !conversation) {
    return <LoadingScreen />;
  }

  return (
    <ConversationActivityIndicatorProvider>
      <view.div
        data-testid="threads-conversation-screen-content"
        css={{
          stack: 'y',
          height: 'fill',
        }}
      >
        <view.div css={{stack: 'y', height: 'fill', overflow: 'auto'}}>
          <ComponentWithOverride<{conversation?: Conversation | null}>
            override={log}
            component={ThreadsConversationLog}
            conversation={conversation}
          />
        </view.div>
        <ComponentWithOverride<{conversation?: Conversation | null}>
          override={input}
          component={ThreadsConversationInput}
          conversation={conversation}
        />
      </view.div>
    </ConversationActivityIndicatorProvider>
  );
}
